import { pdf } from "@react-pdf/renderer";
import { supabase } from "../lib/supabase";
import { PackingSlipDocument, PackingSlipProduct } from "../components/PackingSlip";

interface Booking {
  id: number;
  reference: string;
  transport_type: { name: string; abbreviation: string };
  created_at: string;
  supplier: {
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
    postal_code?: string;
  };
  company: {
    id: number;
    name: string;
    address: string;
    city: string;
    country: string;
    postal_code?: string;
    eori_number?: string;
    coc_number?: string;
  };
  booking_delivery_info?: {
    street_name?: string;
    house_number?: string;
  };
  products: {
    id: number;
    name: string;
    pieces?: number | null;
    cartons?: number | null;
    gross_weight: number;
    cbm: number;
    ean: number | null;
    hs_code?: string | null;
  }[];
}

export const generateAndSavePackingSlip = async (
  booking: Booking
): Promise<string | null> => {
  try {
    // Format the date
    const date = new Date(booking.created_at).toLocaleDateString("en-GB");
    
    // Create reference with transport type (AIR or SEA)
    // Always use the format EFFxxAIR/SEA where xx is the booking ID
    const reference = `EFF${booking.id}${booking.transport_type?.abbreviation || ""}`;

    // Map booking products to packing slip products
    const products: PackingSlipProduct[] = booking.products.map(product => ({
      id: product.id,
      ean: product.ean,
      description: product.name,
      pieces: product.pieces || 0,
      cartons: product.cartons || 0,
      weight: product.gross_weight,
      cbm: product.cbm,
      hsCode: product.hs_code || undefined,
      net_weight: product.gross_weight * 0.9
    }));

    // Create shipFrom and shipTo objects
    const shipFrom = {
      company: booking.supplier.name,
      address: booking.supplier.address,
      city: booking.supplier.city,
      postalCode: booking.supplier.postal_code || "",
      country: booking.supplier.country
    };

    const shipTo = {
      company: booking.company.name,
      address: booking.company.address,
      city: booking.company.city,
      postalCode: booking.company.postal_code || "",
      country: booking.company.country,
      eori_number: booking.company.eori_number || "",
      coc_number: booking.company.coc_number || "",
      street: booking.booking_delivery_info?.street_name || "",
      house_number: booking.booking_delivery_info?.house_number || ""
    };

    // Generate the PDF blob
    const blob = await pdf(
      PackingSlipDocument({
        date,
        reference,
        shipFrom,
        shipTo,
        supplierInfo: {
          name: booking.supplier.name,
          email: booking.supplier.email,
          phone: booking.supplier.phone,
          address: booking.supplier.address,
          country: booking.supplier.country,
          province: booking.supplier.city // Use city as province since we need province for display
        },
        products,
      })
    ).toBlob();

    // Convert blob to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve) => {
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]); // Remove the data URL prefix
      };
    });
    reader.readAsDataURL(blob);
    const base64Data = await base64Promise;

    // Create the file path
    const filePath = `${booking.company.id}/${booking.id}/packing_slip_${booking.id}_${booking.transport_type?.abbreviation || ""}.pdf`;
    console.log("Attempting to upload packing slip to path:", filePath);

    try {
      // Upload to Supabase storage using base64
      const { error: uploadError } = await supabase.storage
        .from("packingslips")
        .upload(filePath, decode(base64Data), {
          contentType: "application/pdf",
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      console.log("Successfully uploaded packing slip to:", filePath);
      return filePath;
    } catch (uploadError) {
      console.error("Error uploading packing slip:", uploadError);
      console.error("Upload error details:", {
        path: filePath,
        companyId: booking.company.id,
        error: uploadError
      });
      return null;
    }
  } catch (error) {
    console.error("Error generating packing slip:", error);
    return null;
  }
};

// Helper function to decode base64
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function generateRegeneratePackingSlipUrl(
  bookingId: number,
  reference: string,
  transportType: string,
  date: string,
  shipFrom: {
    company: string;
    address: string;
    city: string;
    country: string;
    postalCode?: string;
    id?: number;
    email?: string;
    phone?: string;
    province?: string;
  },
  shipTo: {
    company: string;
    address: string;
    city: string;
    country: string;
    postalCode?: string;
    street?: string;
    house_number?: string;
    eori_number?: string;
    coc_number?: string;
  },
  products: {
    id: number;
    name: string;
    pieces: number;
    cartons: number;
    weight: number;
    cbm: number;
    ean: number | null;
    hs_code?: string | null;
  }[],
  companyId: number,
  baseUrl: string = window.location.origin
): string {
  // Create URL with query parameters
  const url = new URL(`${baseUrl}/regenerate-packingslip`);
  
  // Add parameters
  url.searchParams.append('date', date);
  url.searchParams.append('reference', `EFF${bookingId}${transportType}`);
  url.searchParams.append('bookingId', bookingId.toString());
  url.searchParams.append('transportType', transportType);
  url.searchParams.append('companyId', companyId.toString());
  
  // Ship from (supplier info)
  url.searchParams.append('shipFromCompany', shipFrom.company);
  url.searchParams.append('shipFromAddress', shipFrom.address);
  url.searchParams.append('shipFromCity', shipFrom.city);
  url.searchParams.append('shipFromCountry', shipFrom.country);
  if (shipFrom.postalCode) {
    url.searchParams.append('shipFromPostalCode', shipFrom.postalCode);
  }
  
  // Add supplier info with proper naming
  if (shipFrom.id) {
    url.searchParams.append('supplierInfoId', shipFrom.id.toString());
  }
  url.searchParams.append('supplierInfoName', shipFrom.company);
  if (shipFrom.email) {
    url.searchParams.append('supplierInfoEmail', shipFrom.email);
  }
  if (shipFrom.phone) {
    url.searchParams.append('supplierInfoPhone', shipFrom.phone);
  }
  url.searchParams.append('supplierInfoAddress', shipFrom.address);
  url.searchParams.append('supplierInfoCountry', shipFrom.country);
  if (shipFrom.province) {
    url.searchParams.append('supplierInfoProvince', shipFrom.province);
  }
  
  // Ship to
  url.searchParams.append('shipToCompany', shipTo.company);
  url.searchParams.append('shipToAddress', shipTo.address);
  url.searchParams.append('shipToCity', shipTo.city);
  url.searchParams.append('shipToCountry', shipTo.country);
  if (shipTo.postalCode) {
    url.searchParams.append('shipToPostalCode', shipTo.postalCode);
  }
  if (shipTo.street) {
    url.searchParams.append('shipToStreet', shipTo.street);
  }
  if (shipTo.house_number) {
    url.searchParams.append('shipToHouseNumber', shipTo.house_number.toString());
  }
  if (shipTo.eori_number) {
    url.searchParams.append('shipToEoriNumber', shipTo.eori_number);
  }
  if (shipTo.coc_number) {
    url.searchParams.append('shipToCocNumber', shipTo.coc_number.toString());
  }
  
  // Products - use arrays and join with commas
  const productIds = products.map(p => p.ean ? p.ean.toString() : '');
  const descriptions = products.map(p => p.name);
  const pieces = products.map(p => p.pieces.toString());
  const cartons = products.map(p => p.cartons.toString());
  const weights = products.map(p => p.weight.toString());
  const cbms = products.map(p => p.cbm.toString());
  const netWeights = products.map(p => (p.weight * 0.9).toFixed(2));
  const hsCodes = products.map(p => p.hs_code || '');
  
  url.searchParams.append('productIds', productIds.join(','));
  url.searchParams.append('descriptions', descriptions.join(','));
  url.searchParams.append('pieces', pieces.join(','));
  url.searchParams.append('cartons', cartons.join(','));
  url.searchParams.append('weights', weights.join(','));
  url.searchParams.append('cbms', cbms.join(','));
  url.searchParams.append('netWeights', netWeights.join(','));
  url.searchParams.append('hsCodes', hsCodes.join(','));
  
  return url.toString();
} 